/**
 * Canvas Layout Engine
 *
 * Provides hierarchical auto-layout for workflow steps based on dependency
 * depth, layer calculation for layered rendering, and SVG cubic bezier
 * connection path generation between step positions.
 */
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
/** Horizontal spacing (px) between steps within the same layer. */
export const HORIZONTAL_SPACING = 300;
/** Vertical spacing (px) between layers. */
export const VERTICAL_SPACING = 150;
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Calculate optimal positions for workflow steps using a hierarchical layout.
 *
 * Steps are grouped into layers by dependency depth (root steps at layer 0)
 * and evenly distributed horizontally within each layer.
 */
export function autoLayout(workflow) {
    const positions = {};
    if (!workflow.steps || workflow.steps.length === 0) {
        return positions;
    }
    const layers = calculateLayers(workflow);
    layers.forEach((layer, layerIndex) => {
        const layerWidth = layer.length * HORIZONTAL_SPACING;
        const startX = -layerWidth / 2;
        layer.forEach((stepId, indexInLayer) => {
            positions[stepId] = {
                x: startX + indexInLayer * HORIZONTAL_SPACING,
                y: layerIndex * VERTICAL_SPACING,
            };
        });
    });
    return positions;
}
/**
 * Group workflow steps into layers based on dependency depth.
 *
 * Layer 0 contains root steps (no dependencies). Each subsequent layer
 * contains steps whose maximum dependency depth is one less than the
 * current layer index.
 */
export function calculateLayers(workflow) {
    if (!workflow.steps || workflow.steps.length === 0) {
        return [];
    }
    const stepDepths = new Map();
    const calculateDepth = (stepId) => {
        if (stepDepths.has(stepId)) {
            return stepDepths.get(stepId);
        }
        const step = workflow.steps.find((s) => s.id === stepId);
        if (!step || step.depends_on.length === 0) {
            stepDepths.set(stepId, 0);
            return 0;
        }
        const maxDep = Math.max(...step.depends_on.map((depId) => calculateDepth(depId)));
        const depth = maxDep + 1;
        stepDepths.set(stepId, depth);
        return depth;
    };
    // Calculate depths for every step.
    workflow.steps.forEach((step) => calculateDepth(step.id));
    // Build layer arrays.
    const depthValues = Array.from(stepDepths.values());
    const maxDepth = depthValues.length > 0 ? Math.max(...depthValues) : 0;
    const layers = [];
    for (let i = 0; i <= maxDepth; i++) {
        layers[i] = [];
    }
    stepDepths.forEach((depth, stepId) => {
        layers[depth].push(stepId);
    });
    return layers;
}
/**
 * Calculate an SVG cubic bezier path string between two positions.
 *
 * The control points are offset vertically so the curve flows naturally
 * from the source position downward to the target position.
 */
export function calculateConnectionPath(from, to) {
    const dy = to.y - from.y;
    const controlPointOffset = Math.abs(dy) / 2;
    return [
        `M ${from.x} ${from.y}`,
        `C ${from.x} ${from.y + controlPointOffset},`,
        `${to.x} ${to.y - controlPointOffset},`,
        `${to.x} ${to.y}`,
    ].join(' ');
}
